import { useState, useMemo, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useLanguage } from '../contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Checkbox } from '../components/ui/checkbox';
import {
    Accordion, AccordionItem, AccordionTrigger, AccordionContent,
} from '../components/ui/accordion';
import {
    BookOpen, Search, Rocket, Apple, Moon, Dumbbell, HeartPulse,
    Trophy, MessageCircle, Bell, Calendar as CalendarIcon, Shield,
    Sparkles, ExternalLink, ChevronRight, FlaskConical, CheckCircle2,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { HELP_SECTIONS } from '../lib/helpContent';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ICONS = {
    rocket: Rocket,
    apple: Apple,
    moon: Moon,
    dumbbell: Dumbbell,
    heart: HeartPulse,
    lab: FlaskConical,
    trophy: Trophy,
    chat: MessageCircle,
    bell: Bell,
    calendar: CalendarIcon,
    shield: Shield,
    sparkles: Sparkles,
};

export default function Help() {
    const { language } = useLanguage();
    const isDe = language === 'de';
    const [query, setQuery] = useState('');
    const [readSections, setReadSections] = useState(new Set());
    const [badgeUnlocked, setBadgeUnlocked] = useState(false);
    const location = useLocation();

    const sections = useMemo(() => HELP_SECTIONS(isDe), [isDe]);
    const totalSections = sections.length;
    const completed = readSections.size;
    const percent = totalSections ? Math.round((completed / totalSections) * 100) : 0;

    // Fetch progress on mount
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await axios.get(`${API}/help/progress`);
                if (cancelled) return;
                setReadSections(new Set(res.data?.read_sections || []));
                setBadgeUnlocked(!!res.data?.badge_unlocked);
            } catch (err) { console.warn('Failed to load help progress:', err); }
        })();
        return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fetch once on mount
    }, []);

    const toggleRead = useCallback(async (sectionId, nextRead) => {
        // Optimistic update
        setReadSections(prev => {
            const next = new Set(prev);
            if (nextRead) next.add(sectionId); else next.delete(sectionId);
            return next;
        });
        try {
            const res = await axios.post(
                `${API}/help/mark-read`,
                { section_id: sectionId, read: nextRead },
            );
            if (res.data?.badge_just_unlocked) {
                setBadgeUnlocked(true);
                toast.success(
                    isDe
                        ? '🕵️ Tutorial-Detektiv freigeschaltet! +50 XP'
                        : '🕵️ Tutorial Detective unlocked! +50 XP',
                    { duration: 6000 },
                );
            }
        } catch (_e) {
            // Rollback on error
            setReadSections(prev => {
                const next = new Set(prev);
                if (nextRead) next.delete(sectionId); else next.add(sectionId);
                return next;
            });
            toast.error(isDe ? 'Speichern fehlgeschlagen' : 'Could not save progress');
        }
    }, [isDe]);

    // Scroll to anchor on mount (e.g. /help#calendar from Settings deep link)
    useEffect(() => {
        if (!location.hash) return;
        const id = location.hash.replace('#', '');
        const el = document.querySelector(`[data-testid="help-section-${id}"]`);
        if (el) {
            setTimeout(() => {
                el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                el.classList.add('ring-2', 'ring-violet-500/50');
                setTimeout(() => el.classList.remove('ring-2', 'ring-violet-500/50'), 1800);
            }, 200);
        }
    }, [location.hash]);

    const q = query.trim().toLowerCase();
    const filtered = q
        ? sections
              .map(section => ({
                  ...section,
                  items: section.items.filter(it =>
                      (it.q + ' ' + (typeof it.a === 'string' ? it.a : '')).toLowerCase().includes(q)
                  ),
              }))
              .filter(s => s.items.length > 0)
        : sections;

    return (
        <div className="p-4 lg:p-8 pb-24 lg:pb-8" data-testid="help-page">
            <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                className="max-w-3xl mx-auto space-y-6"
            >
                {/* Header */}
                <div className="flex items-start gap-3">
                    <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-violet-500/15 to-indigo-500/15 border border-violet-500/20 flex items-center justify-center flex-shrink-0">
                        <BookOpen className="w-6 h-6 text-violet-600 dark:text-violet-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <h1 className="font-display text-2xl lg:text-3xl font-bold">
                            {isDe ? 'Hilfe & Tutorials' : 'Help & Tutorials'}
                        </h1>
                        <p className="text-sm text-muted-foreground leading-relaxed mt-1">
                            {isDe
                                ? 'Alles, was du über BESTBUDDY wissen musst — kompakt und durchsuchbar.'
                                : 'Everything you need to know about BESTBUDDY — compact and searchable.'}
                        </p>
                    </div>
                </div>

                {/* Search */}
                <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
                    <Input
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder={isDe ? 'Tutorial oder Frage suchen…' : 'Search tutorials or questions…'}
                        className="pl-9"
                        data-testid="help-search-input"
                    />
                </div>

                {/* Quick start strip */}
                {!q && (
                    <Card className="border-violet-500/20 bg-gradient-to-br from-violet-500/5 to-indigo-500/5">
                        <CardContent className="p-4 space-y-3">
                            <div className="flex items-center gap-2">
                                <Sparkles className="w-4 h-4 text-violet-600 dark:text-violet-400" />
                                <p className="text-sm font-semibold">
                                    {isDe ? 'Neu hier? Starte hier:' : 'New here? Start here:'}
                                </p>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs">
                                <Link to="/" className="flex items-center gap-2 px-3 py-2 rounded-lg bg-card border hover:border-violet-500/40 transition-colors" data-testid="help-quick-dashboard">
                                    <Rocket className="w-3.5 h-3.5 text-violet-500" />
                                    <span>{isDe ? '1. Dashboard erkunden' : '1. Explore dashboard'}</span>
                                </Link>
                                <Link to="/nutrition" className="flex items-center gap-2 px-3 py-2 rounded-lg bg-card border hover:border-violet-500/40 transition-colors" data-testid="help-quick-nutrition">
                                    <Apple className="w-3.5 h-3.5 text-emerald-500" />
                                    <span>{isDe ? '2. Erstes Essen loggen' : '2. Log first meal'}</span>
                                </Link>
                                <Link to="/coach" className="flex items-center gap-2 px-3 py-2 rounded-lg bg-card border hover:border-violet-500/40 transition-colors" data-testid="help-quick-coach">
                                    <MessageCircle className="w-3.5 h-3.5 text-indigo-500" />
                                    <span>{isDe ? '3. Mit Nanouk reden' : '3. Chat with Nanouk'}</span>
                                </Link>
                            </div>
                        </CardContent>
                    </Card>
                )}

                {/* Tutorial progress / badge */}
                <Card
                    className={`border ${badgeUnlocked ? 'border-amber-400/40 bg-gradient-to-br from-amber-500/10 to-orange-500/10' : 'border-violet-500/20 bg-card'}`}
                    data-testid="help-progress-card"
                >
                    <CardContent className="p-4 space-y-3">
                        <div className="flex items-center justify-between gap-3">
                            <div className="flex items-center gap-2 min-w-0">
                                {badgeUnlocked ? (
                                    <span className="text-2xl flex-shrink-0" aria-hidden="true">🕵️</span>
                                ) : (
                                    <Trophy className="w-5 h-5 text-violet-600 dark:text-violet-400 flex-shrink-0" />
                                )}
                                <div className="min-w-0">
                                    <p className="text-sm font-semibold truncate" data-testid="help-progress-title">
                                        {badgeUnlocked
                                            ? (isDe ? 'Tutorial-Detektiv freigeschaltet!' : 'Tutorial Detective unlocked!')
                                            : (isDe ? 'Tutorial-Fortschritt' : 'Tutorial progress')}
                                    </p>
                                    <p className="text-[11px] text-muted-foreground truncate">
                                        {badgeUnlocked
                                            ? (isDe ? `+50 XP · alle ${totalSections} Sektionen gelernt 🎉` : `+50 XP · all ${totalSections} sections read 🎉`)
                                            : (isDe
                                                ? `Markiere jede Sektion als gelernt — alle ${totalSections} = +50 XP & Badge`
                                                : `Mark each section as learned — all ${totalSections} = +50 XP & badge`)}
                                    </p>
                                </div>
                            </div>
                            <Badge variant={badgeUnlocked ? 'default' : 'secondary'} className="flex-shrink-0" data-testid="help-progress-counter">
                                {completed}/{totalSections}
                            </Badge>
                        </div>
                        <Progress
                            value={percent}
                            className="h-1.5"
                            data-testid="help-progress-bar"
                        />
                    </CardContent>
                </Card>

                {/* Sections */}
                {filtered.length === 0 && (
                    <Card>
                        <CardContent className="p-6 text-center text-sm text-muted-foreground">
                            {isDe
                                ? 'Keine Tutorials zu deiner Suche gefunden. Versuche ein anderes Stichwort.'
                                : 'No tutorials match your search. Try a different keyword.'}
                        </CardContent>
                    </Card>
                )}

                {filtered.map((section) => {
                    const Icon = ICONS[section.icon] || BookOpen;
                    const isRead = readSections.has(section.id);
                    return (
                        <Card key={section.id} data-testid={`help-section-${section.id}`}>
                            <CardHeader className="pb-2">
                                <CardTitle className="text-base flex items-center gap-2">
                                    <Icon className="w-4 h-4 text-primary flex-shrink-0" />
                                    <span className="flex-1 min-w-0">{section.title}</span>
                                    {section.badge && (
                                        <Badge variant="secondary" className="text-[10px] flex-shrink-0">
                                            {section.badge}
                                        </Badge>
                                    )}
                                    <label
                                        className="flex items-center gap-1.5 text-[11px] font-normal text-muted-foreground cursor-pointer flex-shrink-0 select-none pl-1"
                                        data-testid={`help-section-${section.id}-mark-label`}
                                    >
                                        <Checkbox
                                            checked={isRead}
                                            onCheckedChange={(v) => toggleRead(section.id, !!v)}
                                            data-testid={`help-section-${section.id}-mark`}
                                            className="h-4 w-4"
                                        />
                                        <span className="hidden sm:inline">
                                            {isRead
                                                ? (isDe ? 'Gelernt' : 'Learned')
                                                : (isDe ? 'Als gelernt markieren' : 'Mark as learned')}
                                        </span>
                                        {isRead && (
                                            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 sm:hidden" />
                                        )}
                                    </label>
                                </CardTitle>
                                {section.subtitle && (
                                    <p className="text-xs text-muted-foreground">{section.subtitle}</p>
                                )}
                            </CardHeader>
                            <CardContent className="pt-0">
                                <Accordion type="multiple" className="w-full">
                                    {section.items.map((item, idx) => (
                                        <AccordionItem
                                            key={`${section.id}-${idx}`}
                                            value={`${section.id}-${idx}`}
                                            data-testid={`help-item-${section.id}-${idx}`}
                                        >
                                            <AccordionTrigger className="text-sm font-medium py-3">
                                                {item.q}
                                            </AccordionTrigger>
                                            <AccordionContent>
                                                {typeof item.a === 'string' ? (
                                                    <div className="text-sm text-muted-foreground leading-relaxed whitespace-pre-line">
                                                        {item.a}
                                                    </div>
                                                ) : (
                                                    item.a
                                                )}
                                                {item.cta && (
                                                    <Link to={item.cta.to} className="inline-block mt-3">
                                                        <Button size="sm" variant="outline" className="gap-1.5" data-testid={`help-cta-${section.id}-${idx}`}>
                                                            {item.cta.label}
                                                            <ChevronRight className="w-3.5 h-3.5" />
                                                        </Button>
                                                    </Link>
                                                )}
                                            </AccordionContent>
                                        </AccordionItem>
                                    ))}
                                </Accordion>
                            </CardContent>
                        </Card>
                    );
                })}

                {/* Footer */}
                <Card className="bg-muted/30 border-dashed">
                    <CardContent className="p-4 text-center space-y-2">
                        <p className="text-sm font-medium">
                            {isDe ? 'Noch Fragen?' : 'Still have questions?'}
                        </p>
                        <p className="text-xs text-muted-foreground leading-relaxed">
                            {isDe
                                ? 'Frag Nanouk direkt — sie kennt all deine Daten und kann dir bei vielen Fragen sofort helfen.'
                                : 'Ask Nanouk directly — she knows all your data and can answer most questions instantly.'}
                        </p>
                        <Link to="/coach">
                            <Button size="sm" variant="outline" className="gap-1.5 mt-1" data-testid="help-open-coach">
                                <MessageCircle className="w-3.5 h-3.5" />
                                {isDe ? 'Nanouk fragen' : 'Ask Nanouk'}
                            </Button>
                        </Link>
                        <div className="pt-3 border-t border-border/50 flex items-center justify-center gap-3 text-[11px] text-muted-foreground">
                            <Link to="/legal" className="hover:text-foreground inline-flex items-center gap-1">
                                {isDe ? 'Rechtliches' : 'Legal'}
                                <ExternalLink className="w-3 h-3" />
                            </Link>
                            <span>·</span>
                            <Link to="/trust" className="hover:text-foreground inline-flex items-center gap-1">
                                {isDe ? 'Datenschutz' : 'Privacy'}
                                <ExternalLink className="w-3 h-3" />
                            </Link>
                        </div>
                    </CardContent>
                </Card>
            </motion.div>
        </div>
    );
}
